import { useNavigate } from "react-router-dom";
import { Leaf, CalendarCheck, MessageCircleHeart, Smartphone, ShieldCheck, ArrowRight } from "lucide-react";

const FEATURES = [
  { icon: CalendarCheck, title: "Book in under 3 clicks", body: "See your coach's real-time availability and lock a session in seconds — with instant calendar invites." },
  { icon: MessageCircleHeart, title: "An assistant that never sleeps", body: "Ask nutrition questions anytime. Our gentle AI answers instantly, and knows when to bring your coach in." },
  { icon: Smartphone, title: "Right where you already are", body: "Booking confirmations, reminders and replies flow straight to WhatsApp — no new passwords to remember." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="glass sticky top-0 z-40 px-5 sm:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-2xl zenith-cta flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" strokeWidth={1.5} />
          </span>
          <span className="text-xl font-headline font-medium text-primary tracking-tight">Vitalize</span>
        </div>
        <button
          onClick={() => navigate("/login")}
          className="px-5 py-2.5 rounded-full bg-surface-container-lowest text-primary text-sm font-medium hover:bg-primary-fixed transition-colors duration-300 shadow-ambient"
          data-testid="landing-signin-button"
        >
          Sign in
        </button>
      </header>

      <section className="max-w-5xl mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-rise-in">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-container-lowest text-sm text-primary shadow-ambient">
              <span className="w-2 h-2 rounded-full zenith-cta" /> Health & wellness coaching, reimagined
            </span>
            <h1 className="mt-6 text-5xl sm:text-6xl font-headline font-light text-primary leading-[1.05] tracking-tight">
              Your calmest path to lasting change.
            </h1>
            <p className="mt-6 text-lg text-on-surface-variant leading-relaxed max-w-md">
              Vitalize brings booking, an always-on assistant and your coach together in one breathable space — built for real life, not busywork.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                onClick={() => navigate("/login")}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300 shadow-ambient-lg"
                data-testid="landing-getstarted-button"
              >
                Get started <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-7 py-4 rounded-full bg-surface-container-lowest text-primary font-medium hover:bg-primary-fixed transition-colors duration-300"
                data-testid="landing-book-button"
              >
                Book a session
              </button>
            </div>
          </div>

          <div className="relative animate-rise-in" style={{ animationDelay: "150ms" }}>
            <div className="rounded-3xl overflow-hidden shadow-ambient-lg">
              <img
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="Person meditating peacefully"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-surface-container-lowest rounded-2xl p-5 shadow-ambient-lg w-56">
              <p className="text-xs uppercase tracking-widest text-outline">This week</p>
              <p className="font-headline text-primary text-2xl mt-1">4-day streak</p>
              <p className="text-sm text-on-surface-variant mt-1">You reached your mindfulness goal 🌿</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-10 pb-24">
        <div className="grid sm:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="bg-surface-container-lowest rounded-3xl p-7 shadow-ambient animate-rise-in"
                style={{ animationDelay: `${i * 120}ms` }}
                data-testid="feature-card"
              >
                <span className="w-12 h-12 rounded-2xl bg-primary-fixed flex items-center justify-center">
                  <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-headline text-xl text-primary">{f.title}</h3>
                <p className="mt-2 text-sm text-on-surface-variant leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-10 pb-28">
        <div className="glass-blush rounded-3xl p-10 sm:p-14 text-center shadow-ambient">
          <ShieldCheck className="w-10 h-10 text-secondary mx-auto" strokeWidth={1.5} />
          <h2 className="mt-5 font-headline text-3xl sm:text-4xl text-primary font-light">Private, gentle, and built for you.</h2>
          <p className="mt-4 text-on-surface-variant max-w-xl mx-auto">
            Sign in with Google in one tap — no new passwords. Your data is encrypted and yours alone.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full zenith-cta text-white font-medium hover:opacity-90 transition-opacity duration-300 shadow-ambient-lg"
            data-testid="landing-cta-button"
          >
            Begin your journey <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </section>

      <footer className="text-center py-10 text-sm text-outline">
        © 2026 Vitalize · A calmer way to coach wellness.
      </footer>
    </div>
  );
}
